/**
 * Passenger flow: who arrives at the airport when, every queue they stand
 * in, the lanes that keep those queues short, and the people who will miss
 * their flight if nothing changes.
 *
 * Mirrors the platform's flow/model.py, so the demonstration forecasts as
 * the platform does. Where the established tools stop — a queue measured by
 * a sensor, a wait shown on a screen — this carries on:
 *
 *  1. **It forecasts from the flight record and the plan.** Show-up comes
 *     from each departure's schedule and its profile (domestic, international,
 *     a pilgrim group arrives far earlier); check-in queues come from the
 *     desks the allocation engine actually gave the flight; the arrivals'
 *     immigration wave comes from the predicted on-block and from whether the
 *     stand has a bridge or a bus. A remote stand turns a steady stream into
 *     busloads.
 *  2. **It follows people through the chain.** Desk, then security, then
 *     passport control — each queue's output is the next one's input, so a
 *     slow check-in hall delays the security peak instead of hiding it.
 *  3. **It prescribes.** The fewest lanes or booths that keep each
 *     checkpoint under its target, block by block, opened in time (staff are
 *     called before the peak, not during it), never flickering, compared with
 *     the rostered plan and costed in officer-hours.
 *  4. **It names the flights at risk.** For each departure, the people
 *     predicted to reach the gate after it closes — under the plan as it
 *     stands, and under the recommended one.
 *  5. **It is corrected by what is seen.** A sensor count or a supervisor's
 *     count resets the queue at that moment, and the gap between the model
 *     and the count is shown, not hidden.
 *
 * Pure. No React, no fetch, and `now` is always passed in.
 */
import { t, tn } from "../i18n";
import { hhmm } from "../lib/format";
import { bestTime, phi, type Assignment, type Resource } from "./allocation";
import type { Movement } from "./aodb";

export type Stage = "checkin" | "security" | "emigration" | "immigration";
export type Applies = "all" | "domestic" | "international";
export type Profile = "domestic" | "international" | "group";

export const STAGE_LABEL: Record<Stage, string> = {
  checkin: "Check-in",
  security: "Security",
  emigration: "Passport control out",
  immigration: "Passport control in",
};

export const PROFILE_LABEL: Record<Profile, string> = {
  domestic: "domestic",
  international: "international",
  group: "pilgrim group",
};

/**
 * When people arrive, in minutes before the scheduled departure: a normal
 * curve cut at both ends. PROPOSED profiles until the airport's own counts
 * replace them; the share that queues at a desk rather than checking in
 * online, and a desk's pace for that kind of traveller.
 */
export const PROFILES: Record<Profile, { mean: number; sd: number; min: number; max: number; deskShare: number; deskRate: number }> = {
  domestic: { mean: 95, sd: 30, min: 35, max: 210, deskShare: 0.55, deskRate: 32 },
  international: { mean: 150, sd: 40, min: 50, max: 270, deskShare: 0.8, deskRate: 28 },
  group: { mean: 185, sd: 45, min: 60, max: 320, deskShare: 0.9, deskRate: 26 },
};

export const FLOW = {
  slotMin: 5,
  warmupMin: 120,
  backMin: 60,
  aheadMin: 8 * 60,
  deskToSecurityMin: 5,
  showUpToSecurityMin: 5,
  securityToEmigrationMin: 5,
  gateCloseBeforeMin: 15,
  deskTargetMin: 20,
  deskOpenBefore: 180,
  deskCloseBefore: 45,
  staffLeadMin: 15,
  holdMin: 30,
  blockMin: 15,
  /** people off a bridge per minute, and the bus that carries them from a remote stand */
  deplanePerMin: 40,
  busLoad: 80,
  busEveryMin: 6,
  walkToImmigrationMin: 7,
  groupAt: 250,
  /**
   * With no load on the record, the people are ASSUMED: this share of the
   * seats, or of a typical seat count. Every figure built on it carries
   * `assumed`, so a screen can say so. PROPOSED, like the profiles.
   */
  assumedLoadFactor: 0.8,
  assumedSeats: 150,
};

export interface Checkpoint {
  code: string;
  name: string;
  stage: Exclude<Stage, "checkin">;
  applies: Applies;
  lanesMax: number;
  /** people one open lane or booth clears in an hour */
  ratePerLane: number;
  targetWaitMin: number;
  staffPerLane: number;
  /** minutes from the checkpoint to the gates */
  walkMin: number;
}

export interface LaneStep {
  from: string;
  lanes: number;
}

export interface LanePlan {
  code: string;
  steps: LaneStep[];
  by?: string;
  at?: string;
}

export interface Observation {
  code: string;
  at: string;
  queue?: number;
  waitMin?: number;
  source: "sensor" | "person";
  by?: string;
}

export interface FlowFlight {
  id: string;
  label: string;
  direction: "arrival" | "departure";
  scheduledAt: string;
  /** off-block for a departure, on-block for an arrival: the record's best time */
  at: string;
  basis: string;
  people: number;
  /** no load on the record: `people` is FLOW.assumedLoadFactor of the seats */
  assumed?: boolean;
  transfer: number;
  international: boolean;
  profile: Profile;
  desks?: number;
  deskFrom?: string;
  deskTo?: string;
  /** an arrival on a stand with no bridge */
  bus?: boolean;
  stand?: string;
}

/* ── the flight record and the plan, turned into flights ──────────────── */

const MIN = 60_000;
const ms = (iso: string) => Date.parse(iso);
const iso = (n: number) => new Date(n).toISOString();

export function flightsFrom(
  moves: readonly Movement[],
  plan: readonly Assignment[],
  resources: readonly Resource[],
  domestic: ReadonlySet<string>,
  now: Date,
): FlowFlight[] {
  const res = new Map(resources.map((r) => [r.code, r]));
  const byKey = new Map(plan.map((a) => [a.key, a]));
  /* the stand is keyed by the arrival */
  const posOf = (id: string) => byKey.get(`position:${id}`);
  const out: FlowFlight[] = [];
  for (const m of moves) {
    if (m.status === "cancelled" || m.status === "diverted") continue;
    if ((m.serviceType || "pax") !== "pax" || !m.scheduledAt) continue;
    const b = bestTime(m);
    if (!b) continue;
    const loaded = m.loads?.actual ?? m.loads?.booked;
    const assumed = loaded == null;
    const people = loaded ?? Math.round((m.capacity ?? FLOW.assumedSeats) * FLOW.assumedLoadFactor);
    const international = !isDomestic(m.counterpartNode, domestic);
    const profile: Profile = international ? (people >= FLOW.groupAt ? "group" : "international") : "domestic";
    const f: FlowFlight = {
      id: m.id,
      label: `${m.carrier}${m.number}`,
      direction: m.direction,
      scheduledAt: m.scheduledAt,
      at: m.direction === "departure" ? (m.milestones?.left_position ?? b.at) : (m.milestones?.reached_position ?? iso(ms(b.at) + 5 * MIN)),
      basis: b.basis,
      people,
      assumed,
      transfer: Math.min(people, m.loads?.transfer ?? 0),
      international,
      profile,
    };
    if (m.direction === "departure") {
      const desks = byKey.get(`counter:${m.id}`);
      if (desks) {
        f.desks = desks.segments[0].codes.length;
        f.deskFrom = desks.segments[0].from;
        f.deskTo = desks.segments[0].to;
      }
    } else {
      const pos = posOf(m.id);
      const code = pos?.segments[0].codes[0];
      if (code) {
        f.stand = code;
        f.bus = !res.get(code)?.servedInPlace;
      }
    }
    out.push(f);
  }
  void now;
  return out;
}

/**
 * Whether the other end is domestic. A feed may write IATA (DMM) or ICAO
 * (OEDF) — the record keeps what it was sent — so the list takes both, and
 * an entry ending in * is a prefix: "OE*" is every Saudi aerodrome.
 */
export function isDomestic(node: string | undefined, domestic: ReadonlySet<string>): boolean {
  const n = (node ?? "").toUpperCase();
  if (!n) return false;
  if (domestic.has(n)) return true;
  for (const d of domestic) if (d.endsWith("*") && n.startsWith(d.slice(0, -1))) return true;
  return false;
}

/* ── the arithmetic ───────────────────────────────────────────────────── */

export interface Window {
  start: number;
  slots: number;
  /** the first slot shown; before it, the model only warms up */
  shownFrom: number;
  nowSlot: number;
}

export function windowFor(now: Date): Window {
  const step = FLOW.slotMin * MIN;
  const start = Math.floor((now.getTime() - (FLOW.backMin + FLOW.warmupMin) * MIN) / step) * step;
  const end = now.getTime() + FLOW.aheadMin * MIN;
  const slots = Math.ceil((end - start) / step);
  return {
    start,
    slots,
    shownFrom: Math.floor(((now.getTime() - FLOW.backMin * MIN) - start) / step),
    nowSlot: Math.floor((now.getTime() - start) / step),
  };
}

const slotOf = (w: Window, at: number) => Math.floor((at - w.start) / (FLOW.slotMin * MIN));
const timeOf = (w: Window, i: number) => w.start + i * FLOW.slotMin * MIN;

/** How many of a flight's people arrive at the airport in each slot. */
export function showUp(w: Window, f: FlowFlight, people: number): number[] {
  const p = PROFILES[f.profile];
  const out = new Array(w.slots).fill(0);
  const sched = ms(f.scheduledAt);
  const lo = phi((p.min - p.mean) / p.sd);
  const hi = phi((p.max - p.mean) / p.sd);
  for (let b = p.min; b < p.max; b += FLOW.slotMin) {
    const mass = (phi((b + FLOW.slotMin - p.mean) / p.sd) - phi((b - p.mean) / p.sd)) / (hi - lo);
    const i = slotOf(w, sched - (b + FLOW.slotMin / 2) * MIN);
    if (i >= 0 && i < w.slots) out[i] += people * mass;
  }
  return out;
}

export interface SimSlot {
  arrivals: number;
  lanes: number;
  queue: number;
  served: number;
  waitMin: number;
}

/**
 * A queue slot by slot: what arrives, what the open lanes clear, what is
 * left. The wait is for somebody arriving in the middle of the slot. Where
 * the lanes are shut and open later, the wait includes the time until they
 * open. An observation replaces the model's queue at its slot.
 */
export function simulate(
  arrivals: readonly number[],
  lanes: readonly number[],
  ratePerLane: number,
  overrides: ReadonlyMap<number, number> = new Map(),
): SimSlot[] {
  const out: SimSlot[] = [];
  let q = 0;
  for (let i = 0; i < arrivals.length; i++) {
    if (overrides.has(i)) q = overrides.get(i)!;
    const a = arrivals[i];
    const l = lanes[i];
    const perMin = (l * ratePerLane) / 60;
    let wait: number;
    if (perMin > 0) wait = (q + a / 2) / perMin;
    else {
      const next = lanes.findIndex((x, j) => j > i && x > 0);
      wait = next < 0 ? 999 : (next - i) * FLOW.slotMin + (q + a / 2) / ((lanes[next] * ratePerLane) / 60);
    }
    const cap = perMin * FLOW.slotMin;
    const served = Math.min(q + a, cap);
    q = q + a - served;
    out.push({ arrivals: a, lanes: l, queue: q, served, waitMin: Math.min(999, wait) });
  }
  return out;
}

/** Shift a stream of people by each slot's wait plus a walk, into another stream. */
function passOn(w: Window, stream: readonly number[], sim: readonly SimSlot[], walkMin: number): number[] {
  const out = new Array(w.slots).fill(0);
  for (let i = 0; i < stream.length; i++) {
    if (!stream[i]) continue;
    const j = i + Math.round((sim[i].waitMin + walkMin) / FLOW.slotMin);
    if (j < w.slots) out[j] += stream[i];
  }
  return out;
}

export function lanesFor(w: Window, plan: LanePlan | undefined, fallback: number): number[] {
  const steps = [...(plan?.steps ?? [])].sort((a, b) => a.from.localeCompare(b.from));
  return Array.from({ length: w.slots }, (_, i) => {
    const at = timeOf(w, i);
    let l = steps.length ? steps[0].lanes : fallback;
    for (const s of steps) if (ms(s.from) <= at) l = s.lanes;
    return l;
  });
}

/* ── the forecast ─────────────────────────────────────────────────────── */

export interface FlowInput {
  flights: readonly FlowFlight[];
  checkpoints: readonly Checkpoint[];
  plans: readonly LanePlan[];
  observations: readonly Observation[];
  now: Date;
}

export interface Slot {
  at: string;
  arrivals: number;
  lanes: number;
  queue: number;
  waitMin: number;
  recLanes: number;
  recWaitMin: number;
  /** the flights with most people arriving in this slot */
  top: { label: string; n: number }[];
}

export interface Change {
  at: string;
  callBy: string;
  from: number;
  to: number;
  why: string;
}

export interface CheckpointForecast {
  code: string;
  slots: Slot[];
  changes: Change[];
  peak: { at: string; waitMin: number } | null;
  recPeak: { at: string; waitMin: number } | null;
  minutesOverTarget: number;
  recMinutesOverTarget: number;
  staffHours: number;
  recStaffHours: number;
  nowcast: { at: string; observed: number; predicted: number; source: string } | null;
  people: number;
}

export interface DeskForecast {
  flightId: string;
  label: string;
  profile: Profile;
  desks: number;
  deskFrom: string;
  deskTo: string;
  people: number;
  peakWaitMin: number;
  peakAt: string | null;
  needDesks: number;
  lateForDesks: number;
  allocated: boolean;
  /** the people are assumed from the seats, not a load on the record */
  assumed: boolean;
}

export interface AtRisk {
  flightId: string;
  label: string;
  departureAt: string;
  people: number;
  recPeople: number;
  where: string;
  why: string;
  assumed: boolean;
}

export interface ArrivalForecast {
  flightId: string;
  label: string;
  onBlock: string;
  people: number;
  bus: boolean;
  stand?: string;
  waitMin: number | null;
  assumed: boolean;
}

export interface FlowForecast {
  now: string;
  shownFrom: string;
  checkpoints: CheckpointForecast[];
  desks: DeskForecast[];
  atRisk: AtRisk[];
  arrivals: ArrivalForecast[];
  departures: {
    flightId: string;
    label: string;
    profile: Profile;
    people: number;
    showUpPeak: string | null;
    international: boolean;
    assumed: boolean;
  }[];
}

function matches(cp: Checkpoint, f: FlowFlight): boolean {
  return cp.applies === "all" || (cp.applies === "international") === f.international;
}

/** The first checkpoint of a stage that serves this flight. */
function checkpointFor(cps: readonly Checkpoint[], stage: Checkpoint["stage"], f: FlowFlight): Checkpoint | undefined {
  return cps.find((c) => c.stage === stage && matches(c, f));
}

function deskStream(w: Window, f: FlowFlight) {
  const p = PROFILES[f.profile];
  const local = Math.max(0, f.people - f.transfer);
  const at = showUp(w, f, local);
  const checkers = at.map((x) => x * p.deskShare);
  const walkers = at.map((x) => x * (1 - p.deskShare));
  const sched = ms(f.scheduledAt);
  const from = f.deskFrom ? ms(f.deskFrom) : sched - FLOW.deskOpenBefore * MIN;
  const to = f.deskTo ? ms(f.deskTo) : sched - FLOW.deskCloseBefore * MIN;
  const need = (desks: number) =>
    Array.from({ length: w.slots }, (_, i) => (timeOf(w, i) >= from && timeOf(w, i) < to ? desks : 0));
  return { checkers, walkers, from, to, need, rate: p.deskRate, local };
}

/** The fewest desks keeping the flight's check-in wait under the target, capped at eight. */
function desksNeeded(w: Window, s: ReturnType<typeof deskStream>): number {
  for (let d = 1; d <= 8; d++) {
    const sim = simulate(s.checkers, s.need(d), s.rate);
    const open = sim.filter((_, i) => timeOf(w, i) >= s.from && timeOf(w, i) < s.to);
    if (Math.max(0, ...open.map((x) => x.waitMin)) <= FLOW.deskTargetMin) return d;
  }
  return 8;
}

/**
 * The fewest lanes keeping the wait under target, block by block, from the
 * first block staff can still be called for. Once opened a lane stays open
 * for the hold time; the past keeps the lanes it had.
 */
export function recommend(
  w: Window,
  arrivals: readonly number[],
  current: readonly number[],
  cp: Checkpoint,
  overrides: ReadonlyMap<number, number>,
): number[] {
  const lanes = [...current];
  const block = FLOW.blockMin / FLOW.slotMin;
  const first = w.nowSlot + Math.ceil(FLOW.staffLeadMin / FLOW.slotMin);
  let lastChange = -Infinity;
  for (let b = first; b < w.slots; b += block) {
    const end = Math.min(w.slots, b + block);
    const prev = lanes[b - 1] ?? current[b];
    let chosen = cp.lanesMax;
    for (let l = 1; l <= cp.lanesMax; l++) {
      for (let i = b; i < end; i++) lanes[i] = l;
      /*
       * Look ahead the hold time too: the queue a block leaves behind counts,
       * and officers are not sent away a quarter of an hour before a wave
       * they would have to be called back for.
       */
      const ahead = Math.max(block, FLOW.holdMin / FLOW.slotMin);
      for (let i = end; i < Math.min(w.slots, end + ahead); i++) lanes[i] = l;
      const sim = simulate(arrivals, lanes, cp.ratePerLane, overrides);
      const worst = Math.max(...sim.slice(b, Math.min(w.slots, end + ahead)).map((x) => x.waitMin));
      if (worst <= cp.targetWaitMin) {
        chosen = l;
        break;
      }
    }
    /* never close a lane opened less than the hold time ago */
    if (chosen < prev && (b - lastChange) * FLOW.slotMin < FLOW.holdMin) chosen = prev;
    if (chosen !== prev) lastChange = b;
    for (let i = b; i < w.slots; i++) lanes[i] = chosen;
  }
  return lanes;
}

function steps(lanes: readonly number[], w: Window): LaneStep[] {
  const out: LaneStep[] = [];
  for (let i = 0; i < lanes.length; i++)
    if (i === 0 || lanes[i] !== lanes[i - 1]) out.push({ from: iso(timeOf(w, i)), lanes: lanes[i] });
  return out;
}

export function forecast(inp: FlowInput): FlowForecast {
  const w = windowFor(inp.now);
  const deps = inp.flights.filter((f) => f.direction === "departure");
  const arrs = inp.flights.filter((f) => f.direction === "arrival");

  /* 1. check-in, per flight: its own desks, its own queue */
  const desks: DeskForecast[] = [];
  const toSecurity = new Map<string, number[]>();
  for (const f of deps) {
    const s = deskStream(w, f);
    const n = f.desks ?? desksNeeded(w, s);
    const sim = simulate(s.checkers, s.need(n), s.rate);
    const leaving = passOn(w, s.checkers, sim, FLOW.deskToSecurityMin);
    /* those with nothing to check in walk straight to security */
    const shift = Math.round(FLOW.showUpToSecurityMin / FLOW.slotMin);
    toSecurity.set(f.id, leaving.map((x, i) => x + (i - shift >= 0 ? s.walkers[i - shift] : 0)));
    const open = sim.map((x, i) => ({ ...x, i })).filter((x) => timeOf(w, x.i) >= s.from && timeOf(w, x.i) < s.to && x.arrivals > 0.2);
    const peak = open.reduce<{ waitMin: number; i: number } | null>((m, x) => (!m || x.waitMin > m.waitMin ? x : m), null);
    const late = s.checkers.reduce((n2, x, i) => n2 + (timeOf(w, i) >= s.to ? x : 0), 0);
    if (ms(f.scheduledAt) > inp.now.getTime() - 30 * MIN)
      desks.push({
        flightId: f.id,
        label: f.label,
        profile: f.profile,
        desks: n,
        deskFrom: iso(s.from),
        deskTo: iso(s.to),
        people: Math.round(s.local * PROFILES[f.profile].deskShare),
        peakWaitMin: Math.round(peak?.waitMin ?? 0),
        peakAt: peak ? iso(timeOf(w, peak.i)) : null,
        needDesks: desksNeeded(w, s),
        lateForDesks: Math.round(late),
        allocated: f.desks != null,
        assumed: Boolean(f.assumed),
      });
  }

  /* 2. the checkpoints, in the order people meet them */
  const results: CheckpointForecast[] = [];
  const exitCur = new Map<string, number[]>();
  const exitRec = new Map<string, number[]>();
  /* the checkpoint that holds each departure up longest: where its people are, when they are late */
  const worstAt = new Map<string, { code: string; wait: number }>();
  const order: Checkpoint["stage"][] = ["security", "emigration", "immigration"];
  const incoming = new Map<string, { cur: number[]; rec: number[] }>();
  /* each flight's arrivals at each checkpoint: who makes the busiest slot busy, and each arrival's wait */
  const atCp = new Map<string, number[]>();
  for (const f of deps) {
    const s = toSecurity.get(f.id)!;
    incoming.set(f.id, { cur: s, rec: s });
  }
  for (const f of arrs) {
    if (!f.international || ms(f.at) < w.start || ms(f.at) > w.start + w.slots * FLOW.slotMin * MIN) continue;
    incoming.set(f.id, { cur: deplane(w, f), rec: deplane(w, f) });
  }

  for (const stage of order) {
    for (const cp of inp.checkpoints.filter((c) => c.stage === stage)) {
      const served = inp.flights.filter((f) => {
        if (!incoming.has(f.id) || checkpointFor(inp.checkpoints, stage, f)?.code !== cp.code) return false;
        if (stage === "immigration") return f.direction === "arrival";
        if (f.direction !== "departure") return false;
        return stage === "security" || f.international;
      });
      for (const f of served) atCp.set(`${cp.code}:${f.id}`, incoming.get(f.id)!.cur);
      const sum = (pick: "cur" | "rec") => {
        const a = new Array(w.slots).fill(0);
        for (const f of served) incoming.get(f.id)![pick].forEach((x, i) => (a[i] += x));
        return a;
      };
      const aCur = sum("cur");
      const aRec = sum("rec");
      const current = lanesFor(w, inp.plans.find((p) => p.code === cp.code), Math.ceil(cp.lanesMax / 2));
      const overrides = new Map<number, number>();
      let nowcast: CheckpointForecast["nowcast"] = null;
      const obs = inp.observations
        .filter((o) => o.code === cp.code && ms(o.at) <= inp.now.getTime() && ms(o.at) >= inp.now.getTime() - 30 * MIN)
        .sort((a, b) => b.at.localeCompare(a.at))[0];
      const pre = simulate(aCur, current, cp.ratePerLane);
      if (obs) {
        const i = slotOf(w, ms(obs.at));
        if (i >= 0 && i < w.slots) {
          const q = obs.queue ?? ((obs.waitMin ?? 0) * current[i] * cp.ratePerLane) / 60;
          overrides.set(i, q);
          nowcast = { at: obs.at, observed: Math.round(q), predicted: Math.round(pre[Math.max(0, i - 1)]?.queue ?? 0), source: obs.source };
        }
      }
      const simCur = simulate(aCur, current, cp.ratePerLane, overrides);
      const rec = recommend(w, aRec, current, cp, overrides);
      const simRec = simulate(aRec, rec, cp.ratePerLane, overrides);

      /* each flight's people leave this checkpoint when its wait says, and walk on */
      const walk = stage === "security" ? FLOW.securityToEmigrationMin : cp.walkMin;
      for (const f of served) {
        const inc = incoming.get(f.id)!;
        const n = inc.cur.reduce((a, b) => a + b, 0);
        const avg = n ? inc.cur.reduce((a, x, i) => a + x * simCur[i].waitMin, 0) / n : 0;
        if (f.direction === "departure" && avg >= (worstAt.get(f.id)?.wait ?? -1)) worstAt.set(f.id, { code: cp.code, wait: avg });
        const nextCur = passOn(w, inc.cur, simCur, walk);
        const nextRec = passOn(w, inc.rec, simRec, walk);
        incoming.set(f.id, { cur: nextCur, rec: nextRec });
        if (f.direction === "departure" && (stage === "emigration" || !f.international || !checkpointFor(inp.checkpoints, "emigration", f))) {
          exitCur.set(f.id, passOn(w, inc.cur, simCur, cp.walkMin));
          exitRec.set(f.id, passOn(w, inc.rec, simRec, cp.walkMin));
        }
      }

      /* who is in the queue at slot i: the people who arrived over the half hour up to it */
      const contributors = (i: number) =>
        served
          .map((f) => {
            const st = atCp.get(`${cp.code}:${f.id}`) ?? [];
            let n = 0;
            for (let k = Math.max(0, i - 6); k <= i; k++) n += st[k] ?? 0;
            return { label: f.label, n: Math.round(n) };
          })
          .filter((x) => x.n > 0)
          .sort((a, b) => b.n - a.n)
          .slice(0, 3);

      const slots: Slot[] = [];
      for (let i = w.shownFrom; i < w.slots; i++)
        slots.push({
          at: iso(timeOf(w, i)),
          arrivals: Math.round(aCur[i]),
          lanes: current[i],
          queue: Math.round(simCur[i].queue),
          waitMin: Math.round(simCur[i].waitMin),
          recLanes: rec[i],
          recWaitMin: Math.round(simRec[i].waitMin),
          top: contributors(i),
        });
      const future = slots.filter((s) => ms(s.at) >= inp.now.getTime());
      const peakOf = (k: "waitMin" | "recWaitMin") =>
        future.reduce<{ at: string; waitMin: number } | null>((m, s) => (!m || s[k] > m.waitMin ? { at: s.at, waitMin: s[k] } : m), null);
      const over = (k: "waitMin" | "recWaitMin") => future.filter((s) => s[k] > cp.targetWaitMin).length * FLOW.slotMin;
      const hours = (k: "lanes" | "recLanes") => (future.reduce((n, s) => n + s[k], 0) * FLOW.slotMin * cp.staffPerLane) / 60;
      results.push({
        code: cp.code,
        slots,
        changes: changes(w, cp, current, rec, simCur, contributors, inp.now),
        peak: peakOf("waitMin"),
        recPeak: peakOf("recWaitMin"),
        minutesOverTarget: over("waitMin"),
        recMinutesOverTarget: over("recWaitMin"),
        staffHours: Math.round(hours("lanes") * 10) / 10,
        recStaffHours: Math.round(hours("recLanes") * 10) / 10,
        nowcast,
        people: Math.round(future.reduce((n, s) => n + s.arrivals, 0)),
      });
    }
  }

  /* 3. who misses the gate */
  const atRisk: AtRisk[] = [];
  for (const f of deps) {
    const cur = exitCur.get(f.id);
    const rec = exitRec.get(f.id);
    if (!cur || !rec) continue;
    const close = ms(f.at) - FLOW.gateCloseBeforeMin * MIN;
    if (close < inp.now.getTime()) continue;
    const late = (s: number[]) => s.reduce((n, x, i) => n + (timeOf(w, i) > close ? x : 0), 0);
    const nCur = Math.round(late(cur));
    const nRec = Math.round(late(rec));
    const held = worstAt.get(f.id);
    if (nCur >= 1)
      atRisk.push({
        flightId: f.id,
        label: f.label,
        departureAt: f.at,
        people: nCur,
        recPeople: nRec,
        where: held?.code ?? "",
        assumed: Boolean(f.assumed),
        why: t("{n} predicted to reach the gate after it closes at {close}; the longest wait on their way is {wait} min at {where}.", {
          n: nCur,
          close: hhmm(iso(close)),
          wait: Math.round(held?.wait ?? 0),
          where: held?.code ?? "",
        }),
      });
  }
  atRisk.sort((a, b) => b.people - a.people);

  /* 4. the arrivals' side */
  const imm = results.filter((r) => inp.checkpoints.find((c) => c.code === r.code)?.stage === "immigration");
  const arrivals: ArrivalForecast[] = arrs
    .filter((f) => ms(f.at) >= inp.now.getTime() - 30 * MIN && ms(f.at) <= inp.now.getTime() + FLOW.aheadMin * MIN)
    .map((f) => {
      const cp = checkpointFor(inp.checkpoints, "immigration", f);
      const r = imm.find((x) => x.code === cp?.code);
      let wait: number | null = null;
      if (f.international && r) {
        const stream = atCp.get(`${r.code}:${f.id}`) ?? [];
        let n = 0;
        let sum = 0;
        stream.forEach((x, i) => {
          const s = r.slots[i - w.shownFrom];
          if (x > 0 && s) {
            n += x;
            sum += x * s.waitMin;
          }
        });
        wait = n ? Math.round(sum / n) : null;
      }
      return {
        flightId: f.id,
        label: f.label,
        onBlock: f.at,
        people: f.people,
        bus: Boolean(f.bus),
        stand: f.stand,
        waitMin: wait,
        assumed: Boolean(f.assumed),
      };
    })
    .sort((a, b) => a.onBlock.localeCompare(b.onBlock));

  const departures = deps
    .filter((f) => ms(f.scheduledAt) > inp.now.getTime())
    .map((f) => {
      const s = showUp(w, f, 1);
      const i = s.indexOf(Math.max(...s));
      return {
        flightId: f.id,
        label: f.label,
        profile: f.profile,
        people: f.people,
        international: f.international,
        assumed: Boolean(f.assumed),
        showUpPeak: Math.max(...s) > 0 ? iso(timeOf(w, i)) : null,
      };
    })
    .sort((a, b) => (a.showUpPeak ?? "").localeCompare(b.showUpPeak ?? ""));

  return {
    now: inp.now.toISOString(),
    shownFrom: iso(timeOf(w, w.shownFrom)),
    checkpoints: results,
    desks: desks.sort((a, b) => b.peakWaitMin - a.peakWaitMin),
    atRisk,
    arrivals,
    departures,
  };
}

/** People off an arrival towards passport control: a stream off a bridge, busloads off a remote stand. */
export function deplane(w: Window, f: FlowFlight): number[] {
  const out = new Array(w.slots).fill(0);
  const people = Math.max(0, f.people - f.transfer);
  const on = ms(f.at);
  if (f.bus) {
    let left = people;
    let k = 0;
    while (left > 0) {
      const n = Math.min(FLOW.busLoad, left);
      const i = slotOf(w, on + (8 + k * FLOW.busEveryMin + FLOW.walkToImmigrationMin) * MIN);
      if (i >= 0 && i < w.slots) out[i] += n;
      left -= n;
      k++;
    }
    return out;
  }
  const minutes = Math.max(5, people / FLOW.deplanePerMin);
  for (let m = 0; m < minutes; m++) {
    const i = slotOf(w, on + (3 + m + FLOW.walkToImmigrationMin) * MIN);
    if (i >= 0 && i < w.slots) out[i] += people / Math.ceil(minutes);
  }
  return out;
}

function changes(
  w: Window,
  cp: Checkpoint,
  current: readonly number[],
  rec: readonly number[],
  simCur: readonly SimSlot[],
  contributors: (i: number) => { label: string; n: number }[],
  now: Date,
): Change[] {
  const out: Change[] = [];
  /* each step of the recommended plan, from the level before it; the first from the roster */
  for (let i = Math.max(1, w.nowSlot); i < w.slots; i++) {
    const from = i === w.nowSlot || rec[i - 1] === undefined ? current[i] : rec[i - 1];
    const to = rec[i];
    if (from === to) continue;
    const at = timeOf(w, i);
    let why: string;
    if (to > from) {
      /* the worst wait the current plan would bring in the next hour from here */
      const ahead = simCur.slice(i, i + 12);
      const worst = ahead.reduce((m, x, k) => (x.waitMin > m.w ? { w: x.waitMin, k } : m), { w: 0, k: 0 });
      const names = contributors(i + worst.k).map((c) => c.label).join(", ");
      why = t("the current plan lets the wait reach {w} min at {at} (target {target}){names}", {
        w: Math.round(worst.w),
        at: hhmm(iso(timeOf(w, i + worst.k))),
        target: cp.targetWaitMin,
        names: names ? t("; busiest: {names}", { names }) : "",
      });
    } else {
      why = tn((from - to) * cp.staffPerLane, "demand falls; {n} officer freed", "demand falls; {n} officers freed");
    }
    out.push({ at: iso(at), callBy: iso(Math.max(now.getTime(), at - FLOW.staffLeadMin * MIN)), from, to, why });
  }
  return out;
}

/** The recommended plan as steps, to adopt. */
export function recommendedSteps(f: CheckpointForecast): LaneStep[] {
  const w = f.slots;
  const out: LaneStep[] = [];
  for (let i = 0; i < w.length; i++) if (i === 0 || w[i].recLanes !== w[i - 1].recLanes) out.push({ from: w[i].at, lanes: w[i].recLanes });
  return out;
}

export { steps as toSteps };

/* ── what the platform returns ────────────────────────────────────────── */

export interface FlowDay {
  forecast: FlowForecast;
  checkpoints: Checkpoint[];
  plans: LanePlan[];
  observations: Observation[];
  log: { at: string; code: string; what: string; by?: string }[];
  domesticNodes: string[];
  sample: boolean;
}
